import React, { useState, useEffect } from 'react';
import { questionAPI } from '../../utils/api';
import { DashboardLayout } from '../../components/layout';
import { StatusBadge, Modal, Spinner, EmptyState, Badge, Pagination, StatCard } from '../../components/ui';
import toast from 'react-hot-toast';

const SUBJECTS = ['History','Polity','Geography','Economy','Science','General Tamil','General English','Current Affairs','Indian Economy','Environment','Computer Science','Mental Ability','Tamil Nadu GK','Aptitude'];
const GROUPS = ['Common','Group1','Group2','Group2A','Group4'];
const EMPTY = { question_text:'',option_a:'',option_b:'',option_c:'',option_d:'',correct_answer:'',explanation:'',subject:'',exam_group:'',difficulty:'Medium',language:'English' };

export default function AdminQuestions() {
  const [questions, setQuestions] = useState([]);
  const [pg, setPg] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [delId, setDelId] = useState(null);
  const [filters, setFilters] = useState({ subject:'',exam_group:'',difficulty:'',page:1 });

  const load = () => {
    setLoading(true);
    Promise.all([questionAPI.getAll({...filters,limit:15}),questionAPI.getStats()])
      .then(([qr,sr])=>{
        setQuestions(qr.data.questions||[]);
        setPg({ pages: qr.data.pages||1, page: qr.data.page||1 });
        setStats({
          total: sr.data.total||0,
          by_subject: sr.data.bySubject||[],
          by_group: sr.data.byGroup||[],
          by_difficulty: sr.data.byDifficulty||[],
          by_language: sr.data.byLanguage||[],
        });
      })
      .catch((e)=>{ console.error(e); toast.error('Failed to load questions'); })
      .finally(()=>setLoading(false));
  };

  useEffect(()=>{ load(); },[filters]);

  const openAdd = ()=>{ setEditing(null); setForm(EMPTY); setModal(true); };
  const openEdit = q =>{ setEditing(q); setForm({question_text:q.question_text,option_a:q.option_a,option_b:q.option_b,option_c:q.option_c,option_d:q.option_d,correct_answer:q.correct_answer,explanation:q.explanation||'',subject:q.subject,exam_group:q.exam_group,difficulty:q.difficulty,language:q.language}); setModal(true); };
  const f = (k,v) => setForm(p=>({...p,[k]:v}));

  const save = async () => {
    if (!form.question_text||!form.option_a||!form.option_b||!form.option_c||!form.option_d||!form.correct_answer||!form.subject||!form.exam_group) { toast.error('Fill all required fields.'); return; }
    setSaving(true);
    try {
      editing ? await questionAPI.update(editing.id,form) : await questionAPI.add(form);
      toast.success(editing?'Updated!':'Added!');
      setModal(false); load();
    } catch (e) { toast.error(e.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  const del = async id => {
    try { await questionAPI.delete(id); toast.success('Deleted.'); load(); setDelId(null); }
    catch { toast.error('Delete failed.'); }
  };

  return (
    <DashboardLayout title="Question Bank" subtitle={`${(stats?.total||0).toLocaleString()} total questions`} isAdmin
      actions={<button className="btn btn-gold btn-sm" onClick={openAdd}>+ Add Question</button>}>

      {stats&&(
        <div className="g4" style={{ marginBottom:20 }}>
          <StatCard icon="❓" label="Total Questions" value={(stats.total||0).toLocaleString()} color="blue" />
          <StatCard icon="📚" label="Common (All)" value={(stats.by_group?.find(g=>g.exam_group==='Common')?.count||0).toLocaleString()} color="green" />
          <StatCard icon="🌐" label="Subjects" value={stats.by_subject?.length||0} color="gold" />
          <StatCard icon="📊" label="Group Specific" value={(stats.by_group?.filter(g=>g.exam_group!=='Common').reduce((a,b)=>a+parseInt(b.count||0),0)||0).toLocaleString()} color="blue" />
        </div>
      )}

      <div style={{ display:'flex',gap:10,marginBottom:18,flexWrap:'wrap' }}>
        <select className="form-select no-icon" style={{ width:170 }} value={filters.subject} onChange={e=>setFilters(p=>({...p,subject:e.target.value,page:1}))}>
          <option value="">All Subjects</option>{SUBJECTS.map(s=><option key={s}>{s}</option>)}
        </select>
        <select className="form-select no-icon" style={{ width:150 }} value={filters.exam_group} onChange={e=>setFilters(p=>({...p,exam_group:e.target.value,page:1}))}>
          <option value="">All Groups</option>{GROUPS.map(g=><option key={g}>{g}</option>)}
        </select>
        <select className="form-select no-icon" style={{ width:130 }} value={filters.difficulty} onChange={e=>setFilters(p=>({...p,difficulty:e.target.value,page:1}))}>
          <option value="">All Levels</option><option>Easy</option><option>Medium</option><option>Hard</option>
        </select>
        <button className="btn btn-outline btn-sm" onClick={load}>🔄</button>
      </div>

      <div className="card">
        <div className="tbl-wrap">
          <table>
            <thead><tr><th>#</th><th>Question</th><th>Subject</th><th>Group</th><th>Difficulty</th><th>Lang</th><th></th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={7} style={{ textAlign:'center',padding:40 }}><Spinner size={30} color="blue" /></td></tr>
                : questions.length===0 ? <tr><td colSpan={7}><EmptyState icon="❓" title="No questions" action={<button className="btn btn-primary btn-sm" onClick={openAdd}>+ Add First</button>} /></td></tr>
                : questions.map((q,i) => (
                  <tr key={q.id}>
                    <td style={{ fontFamily:'var(--font-mono)',color:'var(--muted)',fontSize:11 }}>{(filters.page-1)*15+i+1}</td>
                    <td style={{ maxWidth:300 }}>
                      <div style={{ fontSize:13,fontWeight:500,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden' }}>{q.question_text}</div>
                      <div style={{ fontSize:11,color:'var(--success)',marginTop:2 }}>✓ {q.correct_answer}: {[q.option_a,q.option_b,q.option_c,q.option_d][['A','B','C','D'].indexOf(q.correct_answer)]?.slice(0,35)}...</div>
                    </td>
                    <td><Badge type="blue">{q.subject}</Badge></td>
                    <td><Badge type={q.exam_group==='Common'?'success':'blue'}>{q.exam_group}</Badge></td>
                    <td><StatusBadge status={q.difficulty} /></td>
                    <td style={{ fontSize:11 }}>{q.language}</td>
                    <td>
                      <div style={{ display:'flex',gap:5 }}>
                        <button className="btn btn-outline btn-sm" onClick={()=>openEdit(q)}>✏️</button>
                        <button className="btn btn-danger btn-sm" onClick={()=>setDelId(q.id)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination page={filters.page} pages={pg.pages||1} onPage={p=>setFilters(f=>({...f,page:p}))} />

      {/* Add/Edit Modal */}
      <Modal open={modal} onClose={()=>setModal(false)} title={editing?'Edit Question':'Add Question'} size={680}
        footer={<><button className="btn btn-outline" onClick={()=>setModal(false)}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving?<><Spinner size={15} color="white"/>Saving...</>:editing?'💾 Update':'+ Add'}</button></>}>
        <div className="g2" style={{ gap:14 }}>
          <div className="form-group" style={{ gridColumn:'1/-1' }}>
            <label className="form-label">Question Text <span className="req">*</span></label>
            <textarea className="form-textarea" placeholder="Enter the question..." value={form.question_text} onChange={e=>f('question_text',e.target.value)} style={{ minHeight:75 }} />
          </div>
          {['A','B','C','D'].map((o,i)=>(
            <div key={o} className="form-group">
              <label className="form-label">Option {o} <span className="req">*</span></label>
              <div className="input-wrap"><span className="input-icon" style={{ fontFamily:'var(--font-mono)',fontWeight:700,color:'var(--blue)',fontSize:13 }}>{o}</span>
                <input className="form-input" placeholder={`Option ${o}`} value={form[`option_${o.toLowerCase()}`]} onChange={e=>f(`option_${o.toLowerCase()}`,e.target.value)} />
              </div>
            </div>
          ))}
          <div className="form-group">
            <label className="form-label">Correct Answer <span className="req">*</span></label>
            <div className="input-wrap"><span className="input-icon">✅</span>
              <select className="form-select" value={form.correct_answer} onChange={e=>f('correct_answer',e.target.value)}>
                <option value="">Select</option><option>A</option><option>B</option><option>C</option><option>D</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Subject <span className="req">*</span></label>
            <div className="input-wrap"><span className="input-icon">📚</span>
              <select className="form-select" value={form.subject} onChange={e=>f('subject',e.target.value)}>
                <option value="">Select</option>{SUBJECTS.map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Exam Group <span className="req">*</span></label>
            <div className="input-wrap"><span className="input-icon">🎯</span>
              <select className="form-select" value={form.exam_group} onChange={e=>f('exam_group',e.target.value)}>
                <option value="">Select</option>{GROUPS.map(g=><option key={g}>{g}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Difficulty</label>
            <div className="input-wrap"><span className="input-icon">📊</span>
              <select className="form-select" value={form.difficulty} onChange={e=>f('difficulty',e.target.value)}>
                <option>Easy</option><option>Medium</option><option>Hard</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Language</label>
            <div className="input-wrap"><span className="input-icon">🌐</span>
              <select className="form-select" value={form.language} onChange={e=>f('language',e.target.value)}>
                <option>English</option><option>Tamil</option>
              </select>
            </div>
          </div>
          <div className="form-group" style={{ gridColumn:'1/-1' }}>
            <label className="form-label">Explanation (optional)</label>
            <textarea className="form-textarea" placeholder="Explain the correct answer..." value={form.explanation} onChange={e=>f('explanation',e.target.value)} style={{ minHeight:65 }} />
          </div>
        </div>
      </Modal>

      {/* Delete confirm */}
      {delId&&(
        <div style={{ position:'fixed',inset:0,background:'rgba(10,20,60,0.5)',backdropFilter:'blur(4px)',zIndex:3000,display:'flex',alignItems:'center',justifyContent:'center',padding:20 }}>
          <div style={{ background:'#fff',borderRadius:20,padding:30,textAlign:'center',maxWidth:360,animation:'scaleIn 0.28s ease' }}>
            <div style={{ fontSize:52,marginBottom:14 }}>🗑️</div>
            <h3 style={{ fontFamily:'var(--font-heading)',fontSize:21,marginBottom:9 }}>Delete Question?</h3>
            <p style={{ color:'var(--muted)',fontSize:13,marginBottom:22 }}>This action cannot be undone.</p>
            <div style={{ display:'flex',gap:10 }}>
              <button className="btn btn-outline btn-lg" style={{ flex:1 }} onClick={()=>setDelId(null)}>Cancel</button>
              <button className="btn btn-danger btn-lg" style={{ flex:1 }} onClick={()=>del(delId)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
